import * as Yup from 'yup';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// hooks
import { Autocomplete, Chip, TextField, IconButton, MenuItem, createFilterOptions } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
// routes
import { paths } from 'src/routes/paths';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFUpload } from 'src/components/hook-form';
import { uploadResumeToS3 } from 'src/services/aws.service';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { getFileExtension } from 'src/utils/get-file-extension';
import { bulkUpload } from 'src/services/interview.service';
import { setProgressQueue } from 'src/store/progressQueueSlice';
import { useRouter } from 'src/routes/hooks';
import { addTag, getJobDetails, getTags } from 'src/services/role.service';
import { getCandidateSources } from 'src/services/candidate.service';
import { SourceInput } from 'src/components/source-input';
import AddManually from '../add-candidate-sections/add-manually';

const excelSourceFormatOptions = [
  {
    label: 'LinkedIn',
    value: 'Linkedin',
    logo: '/assets/icons/brands/linkedin.svg',
  },
  {
    label: 'Naukri',
    value: 'NAUKRI',
    logo: '/assets/icons/brands/naukri.svg',
  },
  // {
  //   label: 'Apna Jobs',
  //   value: 'APNA_JOBS',
  //   logo: '/assets/icons/brands/apna_jobs.svg',
  // },
  {
    label: 'Internshala',
    value: 'INTERNSHALA',
    logo: '/assets/icons/brands/internshala.svg',
  },
  {
    label: 'Others',
    value: 'HIREINTEL',
    logo: '/assets/icons/brands/office.svg',
  },
];

const filter = createFilterOptions();

export default function AddCandidatesView({ jobId }) {
  // router
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const type = queryParams.get('type');

  // hooks
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const orgId = getOrganizationId(user);
  const dispatch = useDispatch();
  const router = useRouter();

  // states
  const [pageTitle, setPageTitle] = useState('Upload');
  const [inputSourceFormat, setInputSourceFormat] = useState('');
  const [tagInputValue, setTagInputValue] = useState('');
  const [inputSourceFormatOptions, setInputSourceFormatOptions] = useState([]);
  const [isFetchingTags, setIsFetchingTags] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [tags, setTags] = useState([]);

  // memo
  const defaultValues = useMemo(
    () => ({
      source: '',
      tags: [],
      files: [],
    }),
    [],
  );

  const schema = Yup.object().shape({
    source: Yup.string(),
    tags: Yup.array(),
    files: Yup.array()
      .min(1, 'Atleast one file is required')
      .of(Yup.mixed().nullable()),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  // variables
  const {
    reset,
    getValues,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const downloadExcelFormat = () => {
    const element = document.createElement('a');
    element.href =
      'https://hi-downloads.s3.amazonaws.com/app-assets/hireintel_format.xlsx';
    element.download = 'hireintel-format.xlsx';

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const addToLocalStorage = ({ bulkRequestId, jobName }) => {
    const bulkRequestObj = {
      bulkRequestId,
      jobId,
      jobName,
      status: '',
      expiration: new Date().getTime() + 300000,
    };

    const progressQueue = JSON.parse(
      localStorage.getItem(`${user.user_id}_progressQueue`),
    );

    if (progressQueue) {
      const progressIndex = progressQueue.findIndex(
        item => item.bulkRequestId === bulkRequestId,
      );

      if (progressIndex === -1) {
        progressQueue.unshift(bulkRequestObj);
        localStorage.setItem(
          `${user.user_id}_progressQueue`,
          JSON.stringify(progressQueue),
        );

        dispatch(setProgressQueue(progressQueue));
      }
    } else {
      localStorage.setItem(
        `${user.user_id}_progressQueue`,
        JSON.stringify([bulkRequestObj]),
      );
      dispatch(setProgressQueue([bulkRequestObj]));
    }
  };

  const onSubmit = handleSubmit(async (data, event) => {
    try {
      const folderName = uuidv4();
      const { files } = data;

      if (type === 'excel') {
        const file = files[0];

        await uploadResumeToS3(
          file,
          folderName,
          file.type,
          getFileExtension(file.name),
        );
      } else if (type === 'resume') {
        const uploadPromises = files.map(async file =>
          uploadResumeToS3(
            file,
            folderName,
            file.type,
            getFileExtension(file.name),
          ),
        );

        await Promise.all(uploadPromises);
      }

      const reqBody = {
        s3_folder_name: `resumes/${folderName}`,
        input_format: inputSourceFormat || 'RESUME',
        bulk_request_id: folderName,
        should_send_email: true,
        candidate_source: data.source,
      };

      if (data.tags.length === 0 && tagInputValue !== '') {
        reqBody.tags = [tagInputValue];
      } else {
        reqBody.tags = data.tags;
      }

      const res = await getJobDetails(orgId, jobId);
      const jobName = res.body.name;

      addToLocalStorage({
        bulkRequestId: folderName,
        jobId,
        jobName,
      });

      await bulkUpload(jobId, reqBody);

      enqueueSnackbar({
        message: `Generating interviews usually takes 30 seconds. Hit the browser's refresh for results.`,
        autoHideDuration: 10000,
      });

      navigate(paths.dashboard.jobs.details(jobId));
    } catch (err) {
      console.log('error: ', err);
      enqueueSnackbar({
        message: 'Something went wrong',
        variant: 'error',
      });
    } finally {
      reset();
    }
  });

  const handleMultipleDrop = useCallback(
    acceptedFiles => {
      const newFiles = acceptedFiles.map(file =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );

      const { files } = getValues();

      if (type === 'resume') {
        setValue('files', [...files, ...newFiles]);
      } else if (type === 'excel') {
        setValue('files', [...newFiles]);
      }
    },
    [getValues, setValue, type],
  );

  const handleRemoveFile = inputFile => {
    const { files } = getValues();
    const filtered = files.filter(file => file !== inputFile);
    setValue('files', filtered);
  };

  const handleRemoveAllFiles = () => {
    setValue('files', []);
  };

  const fetchCandidateSources = useCallback(async () => {
    try {
      const res = await getCandidateSources(orgId);

      if (res.success) {
        setInputSourceFormatOptions(
          res.body.map(item => ({
            label: item,
            value: item,
          })),
        );
      } else throw new Error('Failed to fetch candidate sources');
    } catch (err) {
      console.log('error: ', err);
    }
  }, [orgId]);

  const handleAddNewTag = async newTagInput => {
    try {
      if (!newTagInput) {
        enqueueSnackbar('Tag name cannot be empty!', {
          variant: 'error',
        });
        return;
      }

      const payload = {
        candidate_tag: newTagInput,
      };

      const response = await addTag(orgId, payload);

      if (response.success) {
        await fetchTags();
        enqueueSnackbar('Added new tag successfully');
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar('Failed to add tag', {
        variant: 'error',
      });
    }
  };

  const getSortedTags = useCallback((currentTags, _allTags) => {
    const _sortedTags = [..._allTags];

    // Move selected tags to the beginning
    const selectedTags = _sortedTags.filter(tag => currentTags.includes(tag));
    const unselectedTags = _sortedTags.filter(tag => !currentTags.includes(tag));

    // Sort selected tags alphabetically
    selectedTags.sort((a, b) => a.localeCompare(b));

    // Sort unselected tags alphabetically
    unselectedTags.sort((a, b) => a.localeCompare(b));

    // Combine the sorted arrays
    _sortedTags.length = 0;
    _sortedTags.push(...selectedTags);
    _sortedTags.push(...unselectedTags);

    // Update the state with the sorted tags
    setTags(_sortedTags);

  },[setTags]);

  const fetchTags = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) {
          setIsFetchingTags(true);
        }

        const response = await getTags(orgId);
        if (response.success) {
          setAllTags(response.body);
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        console.log('err', err);
      } finally {
        setIsFetchingTags(false);
      }
    },
    [orgId],
  );

  const renderHeading = (
    <Typography variant="h3" sx={{ mb: '24px' }}>
      {pageTitle}
    </Typography>
  );

  const renderProperties = (
    <Stack spacing="20px">
      <Stack>
        <RHFUpload
          title={`Upload ${type === 'resume' ? 'resume(s)' : 'excel file'}`}
          name="files"
          type={type}
          multiple
          isExcel={type === 'excel'}
          onRemove={handleRemoveFile}
          onRemoveAll={handleRemoveAllFiles}
          onDrop={handleMultipleDrop}
          accept={{
            ...(type === 'resume' && {
              'application/pdf': ['.pdf'],
              'application/msword': ['.doc'],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                ['.docx'],
            }),
            ...(type === 'excel' && {
              'application/vnd.ms-excel': ['.xls'],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                ['.xlsx'],
            }),
          }}
        />
      </Stack>
      <Stack spacing="6px">
        <Typography variant="paragraph" fontWeight={600}>
          Source
        </Typography>
        <SourceInput
          inputSourceFormatOptions={inputSourceFormatOptions}
          setInputSourceFormatOptions={setInputSourceFormatOptions}
          methods={methods}
        />
      </Stack>
      <Stack spacing="6px">
        <Typography variant="paragraph" fontWeight={600}>
          Tags
        </Typography>
        <Controller
            name="tags"
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                multiple
                onOpen={()=> getSortedTags(methods.getValues().tags, tags)}
                loading={isFetchingTags}
                options={
                  tags.length
                    ? tags
                        .map(tag => ({
                          label: tag,
                          value: tag,
                        }))
                    : []
                }
                getOptionLabel={option => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  return option.label;
                }}
                renderOption={(props, option, state, ownerState)=>{
                  const currentTags = methods.getValues().tags;

                  const isTagFound = currentTags ? currentTags.includes(option.label) : false;

                  return (
                    <MenuItem
                    component="li"
                    {...props}
                    selected={isTagFound}

                  >
                       <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography variant="caption">{option.label}</Typography>
                    {isTagFound && <Iconify icon="tabler:check" width={20} />}
                  </Stack>
                    </MenuItem>
                  )

                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  const { inputValue } = params;

                  const isExisting = options.some(
                    option =>
                      inputValue.toLowerCase() === option.label.toLowerCase(),
                  );

                  if (inputValue !== '' && !isExisting) {
                    filtered.push({
                      inputValue,
                      label: `Add "${inputValue}"`,
                    });
                  }

                  return filtered;
                }}
                inputValue={tagInputValue}
                onInputChange={(event, newInputValue) => {
                  if (newInputValue.endsWith(',')) {
                    let oldValues = methods.getValues();
                    oldValues = oldValues.tags;

                    let newValueArr = newInputValue.split(',').slice(0, -1);
                    newValueArr = oldValues.concat(newValueArr);

                    methods.setValue('tags', newValueArr, {
                      shouldValidate: true,
                    });

                    setTagInputValue('');
                  } else {
                    setTagInputValue(newInputValue);
                  }
                }}
                onChange={async (event, newValue) => {
                  const _tags = newValue
                    .map(val => {
                      if(typeof val === 'string'){
                        return val;
                      }
                      if(val?.value){
                        return val.value;
                      }
                      return null
                    })
                    .filter(val => val !== null);

                  if (newValue.some(option => option.inputValue)) {
                    const newTag = newValue.find(option => option.inputValue);

                    const newTagValue = newTag.inputValue
                      .split(' ')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');

                    await handleAddNewTag(newTagValue);
                    await fetchTags();

                    _tags.unshift(newTagValue);

                    methods.setValue('tags', _tags, {
                      shouldValidate: true,
                    });
                  }
                  else{
                    methods.setValue('tags', _tags, {
                      shouldValidate: true,
                    });
                  }
                }}
                isOptionEqualToValue={(option, value) =>  option.value === value}
                renderInput={params => (
                  <TextField
                    placeholder="Add tag and press Enter"
                    error={!!error}
                    {...params}
                    helperText={error ? error?.message : ''}
                  />
                )}
                freeSolo
                disableCloseOnSelect
                handleHomeEndKeys
                autoHighlight
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      color="info"
                      variant="soft"
                    />
                  ))
                }
              />
            )}
          />
      </Stack>
    </Stack>
  );

  const renderActions = (
    <Stack mt="24px">
      <LoadingButton
        loading={isSubmitting}
        type="submit"
        size="large"
        variant="contained"
      >
        Add Candidate
      </LoadingButton>
    </Stack>
  );

  const renderExcelFileSelector = (
    <Stack spacing="10px" alignItems="center" direction="row" pb="20px">
      <Typography fontWeight={600}>I have excel file of</Typography>

      <Autocomplete
        autoFocus
        sx={{
          width: '200px',
        }}
        options={
          excelSourceFormatOptions.length
            ? excelSourceFormatOptions.sort((a, b) =>
                a.label.localeCompare(b.label),
              )
            : []
        }
        getOptionLabel={option => option.label}
        renderOption={(props, option) => (
          <Box
            component="li"
            sx={{ display: 'flex', alignItems: 'center' }}
            {...props}
          >
            <Box
              component="img"
              src={option.logo}
              sx={{ width: 24, height: 24, mr: 1 }}
            />
            <Typography>{option.label}</Typography>
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            type="text"
            variant="outlined"
            placeholder="File type"
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
              form: {
                autoComplete: 'off',
              },
            }}
          />
        )}
        onChange={(event, newValue) => {
          setInputSourceFormat(newValue?.value || '');
        }}
      />

      {inputSourceFormat === 'HIREINTEL' && (
        <Stack direction="row" textAlign="center" gap={0.5} alignItems="center">
          <Typography>convert in</Typography>
          <Box
            component="span"
            onClick={downloadExcelFormat}
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            variant="caption"
          >
            this format
          </Box>
          <Typography>and then upload</Typography>
        </Stack>
      )}
    </Stack>
  );

  // effects
  useEffect(() => {
    if (type) {
      if (type === 'resume') {
        setPageTitle('Upload Resume');
      } else if (type === 'excel') {
        setPageTitle('Upload Excel');
      } else if (type === 'manual') {
        setPageTitle('Add candidate');
      }
    }
  }, [type]);

  useEffect(() => {
    fetchCandidateSources();
  }, [fetchCandidateSources]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    setTags(allTags)
  },[
    allTags
  ])

  return (
    <Stack width="60%" mx="auto" my="40px">
      {renderHeading}
      <IconButton
        sx={{ position: 'absolute', top: 12, right: 16 }}
        onClick={() => router.push(paths.dashboard.jobs.details(jobId))}
      >
        <Iconify width={30} icon="tabler:x" />
      </IconButton>

      {type !== 'manual' && (
        <FormProvider methods={methods} onSubmit={onSubmit}>
          {type === 'excel' && renderExcelFileSelector}
          {type === 'excel' ? (
            inputSourceFormat && (
              <>
                {renderProperties}
                {renderActions}
              </>
            )
          ) : (
            <>
              {renderProperties}
              {renderActions}
            </>
          )}
        </FormProvider>
      )}
      {type === 'manual' && (
        <AddManually
          jobId={jobId}
          inputSourceFormatOptions={inputSourceFormatOptions}
          setInputSourceFormatOptions={setInputSourceFormatOptions}
          isFetchingTags={isFetchingTags}
          allTags={allTags}
          setAllTags={setAllTags}
          tags={tags}
          setTags={setTags}
          handleAddNewTag={handleAddNewTag}
          fetchTags={fetchTags}
          getSortedTags={getSortedTags}
        />
      )}
    </Stack>
  );
}

AddCandidatesView.propTypes = {
  jobId: PropTypes.string.isRequired,
};
